import { useState, useEffect, useCallback } from 'react';
import { BookOpen, Plus, AlertTriangle, X } from 'lucide-react';

import { PageHeader }     from '@/components/layout/PageHeader';
import { Card }           from '@/components/ui/Card';
import { Badge }          from '@/components/ui/Badge';
import { Button }         from '@/components/ui/Button';
import { IconButton }     from '@/components/ui/IconButton';
import { Input }          from '@/components/ui/Input';
import { Select }         from '@/components/ui/Select';
import { Spinner }        from '@/components/ui/Spinner';

import { usePermission }  from '@/hooks/usePermission';
import { Permissions }    from '@/constants/permissions';
import { chequeBookApi }  from '@/api/chequeBook.api';
import { bankAccountApi } from '@/api/bank.api';
import type { BankAccountListDto } from '@/types/bank.types';
import type {
  ChequeBookListDto, ChequeBookDetailDto, ChequeLeafDto, ChequeLeafStatus,
} from '@/types/chequeBook.types';
import { cn } from '@/utils/cn';

/**
 * Cheque books — which numbers we hold, and what became of each one (GAP-001).
 *
 * Not the cheque register. That says what happened to a cheque once it existed; this says which
 * numbers we have, and is the only place a gap in a physical book can be explained.
 *
 * Registering the first book for a bank account turns the control on for that account. Until then
 * the account is unmanaged and any cheque number is accepted — which is how the system behaved
 * before books existed, and why nothing breaks the day this ships.
 */

// Dark variants follow Badge.tsx's convention — the app themes, and a leaf badge that only reads
// in light mode is a status nobody can see half the time.
const LEAF_STYLES: Record<ChequeLeafStatus, { label: string; className: string }> = {
  AVAILABLE: { label: 'Available', className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800' },
  RESERVED:  { label: 'Reserved',  className: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800' },
  USED:      { label: 'Issued',    className: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700' },
  VOID:      { label: 'Void',      className: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800' },
};

export const ChequeBookPage = () => {
  const { hasPermission } = usePermission();
  const canCreate  = hasPermission(Permissions.CHEQUE_BOOK_CREATE);
  const canClose   = hasPermission(Permissions.CHEQUE_BOOK_CLOSE);
  const canVoid    = hasPermission(Permissions.CHEQUE_BOOK_VOID);
  const canRelease = hasPermission(Permissions.CHEQUE_BOOK_RELEASE);

  const [books,   setBooks]   = useState<ChequeBookListDto[]>([]);
  const [banks,   setBanks]   = useState<BankAccountListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  const [detail,        setDetail]        = useState<ChequeBookDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showRegister,  setShowRegister]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await chequeBookApi.getList({ pageSize: 200 });
      setBooks(res.data.data?.items ?? []);
      setError(null);
    } catch {
      setError('Could not load cheque books. Reload the page, or contact your administrator if this continues.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    bankAccountApi.getList({ pageNumber: 1, pageSize: 200 })
      .then(r => setBanks(r.data.data?.items ?? []))
      .catch(() => setBanks([]));
  }, []);

  const openBook = async (id: string) => {
    setDetailLoading(true);
    try {
      const res = await chequeBookApi.getById(id);
      setDetail(res.data.data ?? null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshBoth = async (id: string) => { await load(); await openBook(id); };

  return (
    <div className="flex flex-col h-full min-h-0">
      <PageHeader
        icon={BookOpen}
        title="Cheque Books"
        subtitle="The cheque numbers you hold, and what became of each one. A bank account with no book is unmanaged — any cheque number is accepted. Registering its first book starts the control."
        actions={canCreate && (
          <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => setShowRegister(true)}>
            Register book
          </Button>
        )}
      />

      {/* The header is full-bleed like every other screen, so the body carries its own padding. */}
      <div className="flex-1 overflow-auto p-6 space-y-5">
        {error && (
        <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : books.length === 0 ? (
        <Card className="p-10 text-center">
          <BookOpen className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
            No cheque books registered
          </p>
          <p className="mt-1 text-sm text-slate-500 dark:text-gray-400 max-w-md mx-auto">
            Every bank account currently accepts any cheque number. Register a book to start
            tracking which numbers you hold and account for every one of them.
          </p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {books.map(b => (
            <BookRow key={b.id} book={b} onOpen={() => openBook(b.id)} />
          ))}
        </div>
      )}

      </div>
      {showRegister && (
        <RegisterBookDialog
          banks={banks}
          onClose={() => setShowRegister(false)}
          onSaved={async () => { setShowRegister(false); await load(); }}
        />
      )}

      {(detail || detailLoading) && (
        <BookDetailDrawer
          detail={detail}
          loading={detailLoading}
          canClose={canClose}
          canVoid={canVoid}
          canRelease={canRelease}
          onDismiss={() => setDetail(null)}
          onChanged={refreshBoth}
        />
      )}
    </div>
  );
};

// ── One book in the list ─────────────────────────────────────────────────────

const BookRow = ({ book, onOpen }: { book: ChequeBookListDto; onOpen: () => void }) => (
  <div onClick={onOpen} className="cursor-pointer">
    <Card className="p-4 hover:border-orange-300 transition-colors">
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-800 dark:text-slate-100">{book.bookCode}</span>
          <Badge variant={book.status === 'ACTIVE' ? 'success' : 'default'}>{book.status}</Badge>
        </div>
        <p className="text-sm text-slate-500 dark:text-gray-400 mt-0.5">
          {book.firstChequeNumber}–{book.lastChequeNumber} · {book.bankAccountName} ({book.bankAccountNumber})
        </p>
      </div>

      {/* The counts are why anyone opens this screen: how many are left in this book. */}
      <div className="flex items-center gap-4 text-sm">
        <Count label="Available" value={book.availableLeaves} tone="text-emerald-600 dark:text-emerald-400" />
        <Count label="Reserved"  value={book.reservedLeaves}  tone="text-amber-600 dark:text-amber-400" />
        <Count label="Issued"    value={book.usedLeaves}      tone="text-slate-600 dark:text-gray-400" />
        <Count label="Void"      value={book.voidLeaves}      tone="text-rose-600 dark:text-rose-400" />
      </div>
      </div>
    </Card>
  </div>
);

const Count = ({ label, value, tone }: { label: string; value: number; tone: string }) => (
  <div className="text-center">
    <div className={cn('font-semibold tabular-nums', tone)}>{value}</div>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

// ── Register a book ──────────────────────────────────────────────────────────

const RegisterBookDialog = ({
  banks, onClose, onSaved,
}: { banks: BankAccountListDto[]; onClose: () => void; onSaved: () => void }) => {
  const [bankAccountId, setBankAccountId] = useState('');
  const [bookCode,      setBookCode]      = useState('');
  const [startNumber,   setStartNumber]   = useState('');
  const [endNumber,     setEndNumber]     = useState('');
  const [numberLength,  setNumberLength]  = useState('6');
  const [prefix,        setPrefix]        = useState('');
  const [receivedDate,  setReceivedDate]  = useState('');
  const [remarks,       setRemarks]       = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);

  const start = Number(startNumber), end = Number(endNumber);
  const leafCount = start > 0 && end >= start ? end - start + 1 : 0;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      await chequeBookApi.create({
        bankAccountId,
        bookCode,
        startNumber:  start,
        endNumber:    end,
        numberLength: Number(numberLength) || 6,
        prefix:       prefix || null,
        receivedDate: receivedDate || null,
        remarks:      remarks || null,
      });
      onSaved();
    } catch (e: unknown) {
      // The server's reason, verbatim: it knows which number clashed and which book has it.
      const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
      setError(res?.data?.errors?.[0]?.message ?? res?.data?.message
        ?? 'Could not register the book. Check the details and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-slate-800 dark:text-slate-100">Register a cheque book</h2>
          <IconButton label="Close" variant="bare" size="xs" icon={<X className="w-4 h-4" />} onClick={onClose} />
        </div>

        <p className="text-sm text-slate-500 dark:text-gray-400">
          Enter the range printed on the book you received. Every number in it becomes a leaf you
          can account for.
        </p>

        <Select label="Bank account" value={bankAccountId} onChange={e => setBankAccountId(e.target.value)}>
            <option value="">Select…</option>
            {banks.map(b => (
              <option key={b.id} value={b.id}>{b.accountName} — {b.accountNumber}</option>
            ))}
          </Select>

        <Input label="Book code" value={bookCode} onChange={e => setBookCode(e.target.value)}
                 placeholder="CB-2026-01" />

        <div className="grid grid-cols-2 gap-3">
          <Input label="First cheque no." value={startNumber} onChange={e => setStartNumber(e.target.value)}
                   inputMode="numeric" placeholder="445601" />
          <Input label="Last cheque no." value={endNumber} onChange={e => setEndNumber(e.target.value)}
                   inputMode="numeric" placeholder="445700" />
          <Input label="Digits" value={numberLength} onChange={e => setNumberLength(e.target.value)}
                   inputMode="numeric" />
          <Input label="Prefix (optional)" value={prefix} onChange={e => setPrefix(e.target.value)}
                   placeholder="A" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Received on" type="date" value={receivedDate} onChange={e => setReceivedDate(e.target.value)} />
          <Input label="Remarks" value={remarks} onChange={e => setRemarks(e.target.value)} />
        </div>

        {/* Say what is about to be created. 100 leaves and 10,000 look identical in two number
            boxes, and one of them is a typo. */}
        {leafCount > 0 && (
          <p className="text-sm text-slate-500 dark:text-gray-400">
            This will create <span className="font-medium text-slate-700 dark:text-slate-200">{leafCount}</span>{' '}
            {leafCount === 1 ? 'leaf' : 'leaves'}.
          </p>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving || !bankAccountId || !bookCode || leafCount === 0}>
            {saving ? 'Registering…' : 'Register book'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ── A book and its leaves ────────────────────────────────────────────────────

const BookDetailDrawer = ({
  detail, loading, canClose, canVoid, canRelease, onDismiss, onChanged,
}: {
  detail: ChequeBookDetailDto | null;
  loading: boolean;
  canClose: boolean;
  canVoid: boolean;
  canRelease: boolean;
  onDismiss: () => void;
  onChanged: (id: string) => Promise<void>;
}) => {
  const [voidLeaf,   setVoidLeaf]   = useState<ChequeLeafDto | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [closing,    setClosing]    = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    if (!detail) return;
    setBusy(true); setError(null);
    try {
      await fn();
      await onChanged(detail.id);
      setVoidLeaf(null); setVoidReason(''); setClosing(false);
    } catch (e: unknown) {
      const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
      setError(res?.data?.errors?.[0]?.message ?? res?.data?.message ?? 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" onClick={onDismiss}>
      <div
        className="w-full max-w-2xl h-full overflow-auto bg-white dark:bg-slate-900 p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        {loading || !detail ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-semibold text-slate-800 dark:text-slate-100">{detail.bookCode}</h2>
                <p className="text-sm text-slate-500 dark:text-gray-400">
                  {detail.bankAccountName} ({detail.bankAccountNumber}) · {detail.status}
                </p>
                {detail.remarks && <p className="text-sm text-slate-500 dark:text-gray-400 mt-1">{detail.remarks}</p>}
              </div>
              <div className="flex items-center gap-2">
                {canClose && detail.status === 'ACTIVE' && (
                  <Button variant="secondary" onClick={() => setClosing(true)}>Close book</Button>
                )}
                <IconButton label="Close" variant="bare" size="xs" icon={<X className="w-4 h-4" />} onClick={onDismiss} />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <div className="divide-y divide-slate-100 dark:divide-slate-800 border rounded border-slate-200 dark:border-slate-700">
              {detail.leaves.map(leaf => {
                const style = LEAF_STYLES[leaf.status];
                return (
                  <div key={leaf.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-700 dark:text-slate-200">
                          {leaf.chequeNumber}
                        </span>
                        <span className={cn('text-[11px] px-1.5 py-0.5 rounded border', style.className)}>
                          {style.label}
                        </span>
                      </div>
                      {/* Every non-available leaf says what happened to it. That sentence is the
                          point of the whole screen. */}
                      {leaf.status === 'RESERVED' && leaf.reservedDocumentNumber && (
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">
                          Held by {leaf.reservedDocumentNumber}
                        </p>
                      )}
                      {leaf.status === 'VOID' && leaf.voidReason && (
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">{leaf.voidReason}</p>
                      )}
                      {leaf.status === 'USED' && (
                        <p className="text-xs text-slate-500 dark:text-gray-400 mt-0.5">Issued — see the Cheque Register</p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {canRelease && leaf.status === 'RESERVED' && (
                        <Button variant="ghost" disabled={busy}
                                onClick={() => act(() => chequeBookApi.releaseLeaf(leaf.id))}>
                          Release
                        </Button>
                      )}
                      {canVoid && (leaf.status === 'AVAILABLE' || leaf.status === 'RESERVED') && (
                        <Button variant="ghost" disabled={busy} onClick={() => setVoidLeaf(leaf)}>
                          Void
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Voiding needs a sentence, not a confirmation: the reason IS the record. */}
        {voidLeaf && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
               onClick={() => setVoidLeaf(null)}>
            <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <Card className="p-5 space-y-3">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                Void cheque {voidLeaf.chequeNumber}
              </h3>
              <p className="text-sm text-slate-500 dark:text-gray-400">
                What happened to it? This becomes the answer when someone asks where this number went.
              </p>
              <Input value={voidReason} onChange={e => setVoidReason(e.target.value)}
                     placeholder="Spoiled in the printer and destroyed" autoFocus />
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setVoidLeaf(null)} disabled={busy}>Cancel</Button>
                <Button variant="danger" disabled={busy || !voidReason.trim()}
                        onClick={() => act(() => chequeBookApi.voidLeaf(voidLeaf.id, voidReason.trim()))}>
                  Void cheque
                </Button>
              </div>
            </Card>
            </div>
          </div>
        )}

        {closing && detail && (
          <CloseBookDialog
            busy={busy}
            onCancel={() => setClosing(false)}
            onConfirm={reason => act(() => chequeBookApi.close(detail.id, reason))}
          />
        )}
      </div>
    </div>
  );
};

const CloseBookDialog = ({
  busy, onCancel, onConfirm,
}: { busy: boolean; onCancel: () => void; onConfirm: (reason: string) => void }) => {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
      <Card className="p-5 space-y-3">
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">Close this cheque book</h3>
        <p className="text-sm text-slate-500 dark:text-gray-400">
          Every unused and reserved leaf will be voided with this reason. Cheques already issued are
          not affected — they exist, and the Cheque Register keeps their status.
        </p>
        <Input value={reason} onChange={e => setReason(e.target.value)}
               placeholder="Account closed by the bank" autoFocus />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>
            Close book
          </Button>
        </div>
      </Card>
      </div>
    </div>
  );
};
